import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNotifications, type Notification } from "@/hooks/useNotifications";
import { useNativeHaptics } from "@/hooks/useNativeHaptics";
import { cn } from "@/lib/utils";
import { Heart, MessageCircle, Trophy, Users, Flag, Check, Trash2 } from "lucide-react";
import { motion, useMotionValue, useTransform, PanInfo } from "framer-motion";
import { useState } from "react";

interface NotificationItemPremiumProps {
  notification: Notification;
  onDismiss?: () => void;
}

const getNotificationIcon = (type: string) => {
  const iconClass = "h-5 w-5";
  switch (type) {
    case 'match':
      return <Heart className={cn(iconClass, "text-primary")} />;
    case 'message':
      return <MessageCircle className={cn(iconClass, "text-blue-500")} />;
    case 'achievement':
      return <Trophy className={cn(iconClass, "text-primary")} />;
    case 'follow':
      return <Users className={cn(iconClass, "text-green-500")} />;
    case 'report':
      return <Flag className={cn(iconClass, "text-red-500")} />;
    default:
      return <Heart className={cn(iconClass, "text-muted-foreground")} />;
  }
};

const getNotificationBgColor = (type: string) => {
  switch (type) {
    case 'match':
      return 'bg-primary/10';
    case 'message':
      return 'bg-blue-500/10';
    case 'achievement':
      return 'bg-primary/10';
    case 'follow':
      return 'bg-green-500/10';
    case 'report':
      return 'bg-red-500/10';
    default:
      return 'bg-muted';
  }
};

export const NotificationItemPremium = ({ notification, onDismiss }: NotificationItemPremiumProps) => {
  const navigate = useNavigate();
  const { markAsRead } = useNotifications();
  const haptics = useNativeHaptics();
  const [isRemoving, setIsRemoving] = useState(false);
  
  const x = useMotionValue(0);
  const opacity = useTransform(x, [-150, 0, 150], [0.5, 1, 0.5]);
  const bgMarkRead = useTransform(x, [0, 80], ['transparent', 'hsl(var(--primary) / 0.2)']);
  const bgDelete = useTransform(x, [-80, 0], ['hsl(var(--destructive) / 0.2)', 'transparent']);

  const handleClick = () => {
    haptics.light();
    
    if (!notification.read) {
      markAsRead(notification.id);
    }

    onDismiss?.();

    // Navigate based on notification type and data
    if (notification.data?.path) {
      navigate(notification.data.path);
    } else {
      switch (notification.type) {
        case 'match':
          navigate('/runner-match');
          break;
        case 'message':
          if (notification.data?.roomId) {
            navigate(`/chat?room=${notification.data.roomId}`);
          } else {
            navigate('/chat');
          }
          break;
        case 'achievement':
          navigate('/profile');
          break;
        case 'follow':
          if (notification.data?.userId) {
            navigate(`/profile/${notification.data.userId}`);
          }
          break;
        default:
          break;
      }
    }
  };

  const handleDragEnd = (event: any, info: PanInfo) => {
    const threshold = 80;
    
    if (info.offset.x > threshold) {
      // Swipe right - mark as read
      haptics.medium();
      markAsRead(notification.id);
    } else if (info.offset.x < -threshold) {
      // Swipe left - remove/archive (mark as read and hide)
      haptics.medium();
      setIsRemoving(true);
      markAsRead(notification.id);
    }
  };

  if (isRemoving) {
    return null;
  }

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, x: -20 },
        visible: { opacity: 1, x: 0 }
      }}
      className="relative overflow-hidden"
    >
      {/* Swipe action backgrounds */}
      <div className="absolute inset-0 flex">
        <motion.div 
          className="flex-1 flex items-center justify-start pl-6"
          style={{ backgroundColor: bgMarkRead }}
        >
          <Check className="h-5 w-5 text-primary" />
        </motion.div>
        <motion.div 
          className="flex-1 flex items-center justify-end pr-6"
          style={{ backgroundColor: bgDelete }}
        >
          <Trash2 className="h-5 w-5 text-destructive" />
        </motion.div>
      </div>

      {/* Notification content */}
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.2}
        onDragEnd={handleDragEnd}
        style={{ x, opacity }}
        onClick={handleClick}
        whileTap={{ scale: 0.98 }}
        className={cn(
          "relative px-4 py-3 cursor-pointer transition-colors bg-background",
          !notification.read && "bg-accent/30"
        )}
      >
        <div className="flex gap-3 items-start">
          {/* Icon with colored background */}
          <div className={cn(
            "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
            getNotificationBgColor(notification.type)
          )}>
            {getNotificationIcon(notification.type)}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className={cn(
              "text-sm line-clamp-1",
              !notification.read ? "font-semibold text-foreground" : "font-medium text-foreground/90"
            )}>
              {notification.title}
            </p>
            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
              {notification.message}
            </p>
            <p className="text-[10px] text-muted-foreground/70 mt-1.5">
              {formatDistanceToNow(new Date(notification.created_at), {
                addSuffix: true,
                locale: ptBR,
              })}
            </p>
          </div>
          
          {/* Unread indicator */}
          {!notification.read && (
            <div className="flex-shrink-0 mt-2">
              <div className="h-2.5 w-2.5 rounded-full bg-primary shadow-lg shadow-primary/30" />
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};
